import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { IMAGES, LINKS, scrollToSection } from "@/data/content";

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="topo" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-[#0B1120]">
      <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
        <img
          src={IMAGES.hero}
          alt="Mar azul e paisagem tropical para inspirar sua próxima viagem"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120] via-[#0B1120]/45 to-[#0B1120]/25" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B1120]/70 via-transparent to-transparent" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 min-h-screen flex flex-col justify-end pb-16 sm:pb-24 pt-32"
      >
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          className="mb-6 inline-flex items-center gap-3"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00E5A3] opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#00E5A3]" />
          </span>
          <span className="font-mono text-[11px] sm:text-xs tracking-[0.35em] text-white/80">
            JOÃO GARCEZ · TRAVEL DESIGNER
          </span>
        </motion.div>

        <h1 className="font-display font-extrabold tracking-tight leading-[0.98] text-white text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
          <MaskedLine delay={0.35}>Viagens pensadas</MaskedLine>
          <MaskedLine delay={0.48}>
            para{" "}
            <em className="font-serif italic font-medium text-[#FF5A36]">você.</em>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
          className="mt-6 max-w-xl text-base sm:text-lg text-white/80 leading-relaxed"
        >
          Roteiros personalizados, experiências especiais e viagens planejadas de acordo com o seu perfil.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            href="#destinos"
            data-testid="hero-cta-explore"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("#destinos");
            }}
            className="group inline-flex items-center gap-2 rounded-full bg-white text-[#0B1120] font-semibold px-7 py-3.5 text-sm sm:text-base hover:bg-[#00E5A3] transition-colors duration-300"
          >
            Explore os destinos
            <ArrowDown size={18} className="transition-transform duration-300 group-hover:translate-y-1" />
          </a>
          <a
            href={LINKS.cotacao}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-whatsapp"
            className="inline-flex items-center gap-2 rounded-full border border-white/40 text-white font-semibold px-7 py-3.5 text-sm sm:text-base hover:border-[#25D366] hover:bg-[#25D366] transition-colors duration-300"
          >
            <MessageCircle size={18} />
            Fale comigo no WhatsApp
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="hidden lg:flex absolute bottom-10 right-8 z-10 items-center gap-3 font-mono text-[10px] tracking-[0.3em] text-white/60"
      >
        <span>9 ANOS DE EXPERIÊNCIA</span>
        <span className="h-px w-10 bg-white/40" />
        <span>7 DESTINOS</span>
      </motion.div>
    </section>
  );
}
